import React, {PropTypes} from 'react';
import {reduxForm, Field} from 'redux-form';
import Submitting from 'shared/Submitting.jsx';
import StandardField from 'shared/StandardField.jsx';
import StandardSelectField from 'shared/StandardSelectField.jsx';
import ErrorListContainer from 'shared/error/ErrorListContainer.jsx';
import LocationsPanelContainer from './LocationsPanelContainer.jsx';
import TagPanelContainer from './TagPanelContainer.jsx';
import scrollToFirstError from '../actions';
import DisplayIf from 'shared/DisplayIf.jsx';

const required = value => value ? undefined : 'Required';

const SetupForm = ({
  error,
  pristine,
  submitLabel,
  submitting,
  timeZones,
  themes,
  cards,
  readOnly,
  handleSubmit,
  onCancel,
  onSubmit,
  exportOptions,
  stripeAccountOptions,
  canEditTournamentName,
  statusDisplayName,
  groupName,
  isAllowed,
  permissions,
  onPartnerLoginClick,
  onExitPartnerClick
}) => {
  let canMigratePartnersFromV2 = permissions.find(permission => permission === "CanMigratePartnersFromV2");

  return (  
    <div className="flex center animated fadeIn">    
      <form
        className="col-xs-12 col-lg-8"
        onSubmit={handleSubmit(onSubmit)}>
        <ErrorListContainer />

        <div className="panel panel-primary">
          <div className="panel-heading">
            <h4 className="panel-title">{'General Information'}</h4>
          </div>

          <DisplayIf condition={isAllowed && canMigratePartnersFromV2 && groupName != undefined && groupName !== ''}>
            <div className="col-xs-12">
              <div className="alert alert-danger text-center">
                <div className="row">
                  <div className="col-xs-3 text-left cursor-pointer" style={{fontSize: '10px', fontWeight: 'bold'}}>
                    <button onClick={onPartnerLoginClick} type="button">
                      <i className="fa fa-user" />{` Manage another Partner Hub`}
                    </button>
                  </div>
                  <div className="col-xs-6 text-center">
                    You are managing a tournament for {groupName}
                  </div>
                  <div className="col-xs-3 text-right cursor-pointer" style={{fontSize: '10px', fontWeight: 'bold'}}>
                    <button onClick={onExitPartnerClick} type="button">
                      <i className="fa fa-sign-out" />{` Exit Partner Hub Mode`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </DisplayIf>

          <div className="panel-body">
            <Field
              autoFocus
              name="name"
              type="text"
              placeholder="Name"
              label="Tournament Name"
              component={StandardField}
              tooltip="Use a descriptive name such as 2021 TX State Womens Championship, 2021 USBC Open Championships"
              tooltipPosition="top"
              disabled={readOnly || !canEditTournamentName}
              validate={[ required ]}
            />

            <Field
              name="startDate"
              type="date"
              todayHighlight={true}
              component={StandardField}
              placeholder="Start Date"
              label="Start Date"
              disabled={readOnly}
              validate={[ required ]}
            />

            <Field
              name="endDate"
              type="date"
              todayHighlight={true}
              component={StandardField}
              placeholder="End Date"
              label="End Date"
              disabled={readOnly}
              validate={[ required ]}
            />

            <Field
              name="registrationCloseDate"
              type="date"
              component={StandardField}
              placeholder="Registration Close Date"
              label="Registration Close Date"
              tooltip="Registration closes to the public at 12:00am on the date entered. Choose the day after your tournament registration closes"
              tooltipPosition="top"
              disabled={readOnly}
              validate={[ required ]}
            />

            <Field
              name="defaultAverage"
              type="number"
              step="1"
              min="0"
              max="300"
              component={StandardField}
              placeholder="Default Average"
              label="Default Average"
              tooltip="Enter the average identified in tournament rules if bowler has no qualifying USBC average, i.e. bowling scratch"
              tooltipPosition="top"
              disabled={readOnly}
              validate={[ required ]}
            />

            <Field
              name="timeZone"
              component={StandardSelectField}
              options={timeZones}
              placeholder="Time Zone"
              label="Time Zone"
              simpleValue
              searchable={false}
              disabled={readOnly}
              validate={[ required ]}
            />

            <Field
              name="bccEmail"
              type="text"
              component={StandardField}
              placeholder="BCC email to copy all sent emails"
              label="BCC Email"
              tooltip="BCC will receive copies of all Registration emails sent to the Captain"
              tooltipPosition="top"
              maxLength="250"
              disabled={readOnly}
              validate={[ required ]}
            />

            <Field
              name="sort"
              type="number"
              component={StandardField}
              placeholder="Display order on main registration page"
              label="Tournament Display Sort Order"
              tooltip="Identify the position you want the tournament card to display on the main screen. From left to right: 1st, 2nd, 3rd, etc."
              tooltipPosition="top"
              disabled={readOnly}
            />

            <Field
              name="theme"
              component={StandardSelectField}
              options={themes}
              placeholder="Blue"
              simpleValue
              searchable={false}
              label="Theme Color"
              tooltip="Identify the color of the displayed tournament card"
              tooltipPosition="top"
              disabled={readOnly}
            />

            <DisplayIf condition={groupName && window.GROUP_ENABLED === "true" ? false : false}>
              <Field
                name="cardId"
                component={StandardSelectField}
                options={cards}
                placeholder="Available Images"
                simpleValue
                searchable={false}
                label="Card Image"
                disabled={readOnly}
              />
            </DisplayIf>
          </div>
        </div>

        <DisplayIf condition={isAllowed && groupName && window.GROUP_ENABLED === "true" ? true : false}>
          <TagPanelContainer />
        </DisplayIf>
        
        <LocationsPanelContainer />

        <div className="panel panel-default">
          <div className="panel-heading">
            <h4 className="panel-title">{`Primary Contact`}</h4>
          </div>
          <div className="panel-body">

            <Field
              name="contactName"
              type="text"
              component={StandardField}
              placeholder="Tournament Contact Name"
              disabled={readOnly}
            />

            <Field
              name="contactPhone"
              type="tel"
              component={StandardField}
              placeholder="Phone"
              disabled={readOnly}
            />

            <Field
              name="tournamentPhone"
              type="tel"
              component={StandardField}
              placeholder="Tournament Phone"
              disabled={readOnly}
              tooltip="Phone Number Captains call with questions."
              tooltipPosition="top"
            />

            <Field
              name="contactAddress"
              type="text"
              component={StandardField}
              placeholder="Address"
              disabled={readOnly}
            />

            <Field
              name="contactEmail"
              type="text"
              component={StandardField}
              placeholder="Email"
              disabled={readOnly}
            />

            <Field
              name="membershipId"
              type="text"
              component={StandardField}
              placeholder="Membership ID"
              disabled={readOnly}
            />
            {error && <p className="text-danger text-right"><strong>{error}</strong></p>}
          </div>
        </div>

        <DisplayIf condition={isAllowed && groupName && window.GROUP_ENABLED === "true" ? true : false}>
          <div className="panel panel-default">
            <div className="panel-heading">
              <h4 className="panel-title">{`Stripe Account`}</h4>
            </div>
            <div className="panel-body">
              <Field
                  name="stripeAccountId"
                  component={StandardSelectField}
                  options={stripeAccountOptions}
                  placeholder="Stripe Account"
                  label="Choose your Stripe account:"
                  simpleValue
                  searchable={false}
                  disabled={readOnly}
                  validate={[ required ]}
                />
            </div>
          </div>
        </DisplayIf>

        <div className="panel panel-default">
          <div className="panel-heading">
            <h4 className="panel-title">{`Tournament Management System`}</h4>
          </div>
          <div className="panel-body">
            <Field
                name="exportId"
                component={StandardSelectField}
                options={exportOptions}
                placeholder="Tournament Management System"
                label="Choose your tournament management system:"
                tooltip="Identify the system you will use to manage/run your tournament. Associations select WinLABS"
                tooltipPosition="top"
                simpleValue
                searchable={false}
                disabled={readOnly || (statusDisplayName ? statusDisplayName !== 'Setup' : false)}
                validate={[ required ]}
              />
          </div>
        </div>

        <div className="form-group text-right">
            <button
              type="button"
              className="btn btn-link"
              disabled={submitting}
              onClick={onCancel}
            >Cancel</button>
            {` `}
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={pristine || submitting}
            >{submitting ? <Submitting /> : <i className="fa fa-floppy-o" />} {submitLabel}</button>
          </div>

      </form>
    </div>
  );
};

SetupForm.propTypes = {
  cards: PropTypes.arrayOf(PropTypes.object),
  error: PropTypes.string,
  exportOptions: PropTypes.arrayOf(PropTypes.object),
  handleSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  pristine: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool,
  stripeAccountOptions: PropTypes.arrayOf(PropTypes.object),
  submitLabel: PropTypes.string,
  submitting: PropTypes.bool.isRequired,
  themes: PropTypes.arrayOf(PropTypes.object),
  timeZones: PropTypes.arrayOf(PropTypes.object),
  canEditTournamentName: PropTypes.bool,
  statusDisplayName: PropTypes.string,
  isAllowed: PropTypes.bool,
  onPartnerLoginClick: PropTypes.func,
  onExitPartnerClick: PropTypes.func
};

export default reduxForm({
  form: 'tournamentSetup',
  onSubmitFail: (errors) => scrollToFirstError(errors)
})(SetupForm);



// WEBPACK FOOTER //
// ./src/features/tournament/general/GeneralForm.jsx